/**
 * WEB-09: Host-owned deliverable artifacts announced by
 * `cowork.stream.deliverableArtifact`. The artifact copy survives sandbox
 * pause/destroy, so the deliverable panel downloads it first. The endpoint
 * requires the Bearer token (browser navigation carries none), so downloads go
 * through fetch+blob. Only same-origin `/api/artifacts/<ref>/download` URLs are
 * accepted. WEB-12 P2: entries are also kept in localStorage (bounded, per
 * browser) so the download entry survives a page reload; the server still
 * enforces owner-only access on every download.
 */
const ARTIFACT_DOWNLOAD_URL = /^\/api\/artifacts\/[A-Za-z0-9%._~:-]+\/download$/;
const byKey = new Map<string, string>();
const STORAGE_KEY = 'boclaw.deliverableArtifacts.v1';
const MAX_PERSISTED = 500;
let loaded = false;

function storage(): Storage | null {
  try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; }
}

function loadPersisted(): void {
  if (loaded) return;
  loaded = true;
  try {
    const raw = storage()?.getItem(STORAGE_KEY);
    const entries = raw ? JSON.parse(raw) as unknown : null;
    if (!Array.isArray(entries)) return;
    for (const entry of entries.slice(-MAX_PERSISTED)) {
      if (Array.isArray(entry) && typeof entry[0] === 'string' && isArtifactDownloadUrl(entry[1])) byKey.set(entry[0], entry[1]);
    }
  } catch { /* corrupt or blocked storage: memory only */ }
}

function persist(): void {
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify([...byKey.entries()].slice(-MAX_PERSISTED)));
  } catch { /* quota or blocked storage: memory only */ }
}

const keyOf = (sessionId: string, containerPath: string) => `${sessionId}\u0000${containerPath}`;

export function isArtifactDownloadUrl(value: unknown): value is string {
  return typeof value === 'string' && ARTIFACT_DOWNLOAD_URL.test(value) && !value.includes('..');
}

export function rememberDeliverableArtifact(event: {
  sessionId?: unknown; containerPath?: unknown; downloadUrl?: unknown;
}): void {
  const { sessionId, containerPath, downloadUrl } = event ?? {};
  if (typeof sessionId !== 'string' || !sessionId) return;
  if (typeof containerPath !== 'string' || !containerPath) return;
  if (!isArtifactDownloadUrl(downloadUrl)) return;
  loadPersisted();
  const key = keyOf(sessionId, containerPath);
  byKey.delete(key);
  byKey.set(key, downloadUrl);
  persist();
}

export function getDeliverableArtifactDownloadUrl(sessionId: string | null | undefined, containerPath: string): string | null {
  if (!sessionId) return null;
  loadPersisted();
  return byKey.get(keyOf(sessionId, containerPath)) ?? null;
}

/** Authenticated fetch of an artifact; null on any failure so callers fall back. */
export async function fetchDeliverableArtifactBlob(
  url: string,
  headers: Record<string, string>,
  fetchImpl: typeof fetch = fetch,
): Promise<Blob | null> {
  if (!isArtifactDownloadUrl(url)) return null;
  try {
    const resp = await fetchImpl(url, { headers });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return blob.size > 0 ? blob : null;
  } catch {
    return null;
  }
}
